/**
 * Who can get in.
 *
 * ---------------------------------------------------------------------
 * THE LIST OPENS ON THE PEOPLE WHO CANNOT
 *
 * A club with four hundred members and eleven logins has one job on this
 * screen, and it is the other three hundred and eighty-nine. A list that
 * opened on the eleven would make somebody search for the problem.
 *
 * ---------------------------------------------------------------------
 * THE CODE IS SHOWN ONCE, AND THE SCREEN SAYS SO BEFORE IT IS DISMISSED
 *
 * What the database holds is scrypt; nothing can read a code back,
 * including this page. So it appears in a panel that has to be closed
 * deliberately, spaced into two groups of five because it is about to be
 * read down a telephone, with a button that copies it.
 *
 * Issuing another withdraws the one before it. That is said next to the
 * button rather than discovered afterwards — "I read her the code twice
 * and the first one stopped working" is otherwise a support call.
 *
 * ---------------------------------------------------------------------
 * WHY THERE IS NO ROLE PICKER ON THE MEMBER PATH
 *
 * Letting somebody in and making somebody a controller are different
 * decisions and this screen only makes the first. A club administrator
 * creating four hundred logins should not be one mistyped field away
 * from creating four hundred controllers. Raising a login is a separate
 * act on the row, with its own audit line.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSort, SortTh, SortPicker } from '../lib/useSort.tsx';
import { date as fmtDate, dateTime } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface PersonRow {
  person_id: string;
  display_name: string;
  person_email: string | null;
  app_user_id: string | null;
  login_email: string | null;
  role: string | null;
  status: string | null;
  last_login_at: string | null;
  must_change_password: boolean | null;
  is_locked_out: boolean | null;
  has_login: boolean;
  member_account_id: string | null;
  account_number: string | null;
  category_name: string | null;
  membership_status: string | null;
  live_code_id: string | null;
  live_code_purpose: string | null;
  live_code_last4: string | null;
  live_code_expires_at: string | null;
  live_code_attempts: number | null;
}

interface DepartmentRow {
  id: string;
  code: string;
  name: string;
  parent_id: string | null;
  runs: string | null;
}

interface AuthorityRow {
  app_user_id: string;
  display_name: string;
  role: string;
  department_scope: 'all' | 'listed';
  departments: { id: string; name: string }[];
}

interface Issued {
  who: string;
  email: string;
  code: string;
  expires_in_hours: number;
}

/** ABCDEFGHIJ → ABCDE FGHIJ. It is about to be read aloud. */
function spaced(code: string): string {
  return `${code.slice(0, 5)} ${code.slice(5)}`;
}

export function LoginsPage() {
  const [show, setShow] = useState<'without' | 'with' | 'all'>('without');
  const [membersOnly, setMembersOnly] = useState(true);
  const [q, setQ] = useState('');
  const [issued, setIssued] = useState<Issued | null>(null);

  const params = new URLSearchParams({ show, members_only: String(membersOnly) });
  if (q.trim()) params.set('q', q.trim());

  const { data, error, loading, reload } = useApi<{
    people: PersonRow[];
    counts: {
      with_login: string;
      without_login: string;
      codes_outstanding: string;
      never_signed_in: string;
    };
    departments: DepartmentRow[];
    authority: AuthorityRow[];
    assignments: { module: string; department_id: string }[];
    modules: string[];
  }>(`/v1/logins?${params}`, [show, membersOnly, q]);

  // Not `loading` alone: that unmounts the page on every keystroke in the
  // search box, and would take the code panel with it.
  if (loading && !data) return <Loading />;

  const counts = data?.counts;
  const people = data?.people ?? [];

  const sorted = useSort(
    people,
    {
      name: (p) => p.display_name,
      email: (p) => p.login_email ?? p.person_email,
      membership: (p) => p.account_number,
      state: (p) => (p.has_login ? (p.is_locked_out ? 'locked' : 'in') : 'none'),
      last_signed_in: (p) => p.last_login_at,
    },
    { key: 'name' },
  );

  return (
    <>
      <h1>Who Can Get In</h1>
      <ErrorBanner error={error} />

      {issued && <CodePanel issued={issued} onClose={() => setIssued(null)} />}

      <div className="tiles">
        <Tile label="Can sign in" value={counts?.with_login ?? '—'} />
        <Tile
          label="Cannot yet"
          value={counts?.without_login ?? '—'}
          tone={Number(counts?.without_login ?? 0) > 0 ? 'warning' : 'good'}
        />
        <Tile
          label="Codes outstanding"
          value={counts?.codes_outstanding ?? '—'}
          note="issued and not yet used"
        />
        <Tile
          label="Never signed in"
          value={counts?.never_signed_in ?? '—'}
          note="have a login, have not used it"
        />
      </div>

      <Card
        title="People"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <input placeholder="Name or email" value={q} onChange={(e) => setQ(e.target.value)} />
            <select value={show} onChange={(e) => setShow(e.target.value as typeof show)}>
              <option value="without">Cannot sign in yet</option>
              <option value="with">Already can</option>
              <option value="all">Everybody</option>
            </select>
            <button className="secondary small" onClick={() => setMembersOnly((m) => !m)}>
              {membersOnly ? 'Members only' : 'Everybody on file'}
            </button>
          </div>
        }
        flush
      >
        {people.length === 0 ? (
          <Empty>
            {show === 'without'
              ? 'Everybody here can already sign in.'
              : 'Nobody matches that.'}
          </Empty>
        ) : (
          <div className="table">
            <SortPicker
              s={sorted}
              labels={{
                name: 'Name',
                email: 'Email',
                membership: 'Membership',
                state: 'State',
                last_signed_in: 'Last signed in',
              }}
            />
            <table className="data data--cards">
              <thead>
                <tr>
                  <SortTh s={sorted} col="name">Name</SortTh>
                  <SortTh s={sorted} col="email">Email</SortTh>
                  <SortTh s={sorted} col="membership">Membership</SortTh>
                  <SortTh s={sorted} col="state">State</SortTh>
                  <SortTh s={sorted} col="last_signed_in">Last signed in</SortTh>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((p) => (
                  <PersonLine
                    key={p.person_id}
                    person={p}
                    onIssued={(i) => {
                      setIssued(i);
                      reload();
                    }}
                    onChanged={reload}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="How a Member Gets In">
        <p className="muted small" style={{ marginBottom: 0 }}>
          Give them a login here and the club is shown a ten-character code, once. Read it to
          them, or write it on the slip that goes out with the statement. They put it into
          &ldquo;Forgotten your password?&rdquo; on the sign-in page along with the password they
          want, and they are in.
        </p>
        <p className="muted small" style={{ marginBottom: 0 }}>
          Nothing here sends email yet, which is why the code is handed over rather than posted.
          When a mail channel exists, the same code is what it will send &mdash; the flow does not
          change, only who carries it.
        </p>
      </Card>

      <DepartmentAuthority
        departments={data?.departments ?? []}
        authority={data?.authority ?? []}
        assignments={data?.assignments ?? []}
        modules={data?.modules ?? []}
        onChange={reload}
      />
    </>
  );
}

/**
 * What each manager is the manager of, and which department runs what.
 *
 * Two panels and one idea (0132): HOLDING EVERY DEPARTMENT IS WHAT THE
 * OFFICE IS. A manager left unrestricted is the office and is unchanged;
 * one given departments is a department head, refused every club-wide
 * screen and given their own.
 *
 * The assignment panel is SECOND and cannot be skipped, because granting
 * the tennis professional Racquet Sports does nothing at all until the
 * club has said that Racquet Sports runs the racquets. An unassigned
 * module belongs to the office — which is the safe answer and a
 * baffling one to meet without warning, so the panel says so in words
 * rather than leaving somebody to work it out from a section missing
 * from a sidebar.
 *
 * Managers only. Everybody above one can edit this screen, so a
 * restriction on them would be theatre; DEPT-2 refuses it in the
 * database, and offering the control here would be advertising
 * something that will not save.
 */
function DepartmentAuthority({
  departments,
  authority,
  assignments,
  modules,
  onChange,
}: {
  departments: DepartmentRow[];
  authority: AuthorityRow[];
  assignments: { module: string; department_id: string }[];
  modules: string[];
  onChange: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);

  const save = useMutation(async (userId: string, scope: 'all' | 'listed', ids: string[]) =>
    api(`/v1/logins/${userId}/departments`, {
      method: 'PUT',
      body: { scope, department_ids: ids },
    }));

  const assign = useMutation(async (module: string, departmentId: string | null) =>
    api('/v1/module-departments', {
      method: 'PUT',
      body: { assignments: [{ module, department_id: departmentId }] },
    }));

  const runsOf = (module: string) =>
    assignments.find((a) => a.module === module)?.department_id ?? '';

  return (
    <>
      <Card title="What Each Manager Is the Manager Of">
        <ErrorBanner error={save.error} />
        <p className="muted small">
          A manager left unrestricted is the office: every department, every club-wide
          screen. Give somebody departments and they become the head of those &mdash;
          their own roster, schedule, timesheets, leave and amenity, and none of
          Finance, Payables, Banking, Governance or Settings.
        </p>
        <p className="muted small">
          A change takes effect the next time they sign in.
        </p>

        {authority.length === 0 ? (
          <Empty>This club has no managers yet.</Empty>
        ) : (
          <table className="data data--cards">
            <thead>
              <tr>
                <th>Manager</th>
                <th>Is the manager of</th>
                <th style={{ width: 140 }} />
              </tr>
            </thead>
            <tbody>
              {authority.map((a) => (
                <tr key={a.app_user_id}>
                  <td>{a.display_name}</td>
                  <td>
                    {editing === a.app_user_id ? (
                      <div className="row row--wrap" style={{ gap: 8 }}>
                        {departments.map((d) => (
                          <label key={d.id} className="small" style={{ marginRight: 12 }}>
                            <input
                              type="checkbox"
                              checked={chosen.includes(d.id)}
                              onChange={(e) =>
                                setChosen((c) =>
                                  e.target.checked
                                    ? [...c, d.id]
                                    : c.filter((x) => x !== d.id))}
                            />{' '}
                            {d.name}
                          </label>
                        ))}
                      </div>
                    ) : a.department_scope === 'all' ? (
                      <Status tone="neutral">The whole club</Status>
                    ) : (
                      a.departments.map((d) => d.name).join(', ')
                    )}
                  </td>
                  <td>
                    {editing === a.app_user_id ? (
                      <div className="row" style={{ gap: 6 }}>
                        <button
                          className="small"
                          disabled={save.busy}
                          onClick={async () => {
                            // No departments ticked is the office, said
                            // as a choice rather than as an error.
                            // DEPT-1 refuses "limited to nothing", and
                            // the honest reading of an empty list on
                            // this screen is that somebody meant to
                            // clear the restriction.
                            const ok = await save.run(
                              a.app_user_id,
                              chosen.length === 0 ? 'all' : 'listed',
                              chosen,
                            );
                            if (ok) {
                              setEditing(null);
                              onChange();
                            }
                          }}
                        >
                          Save
                        </button>
                        <button className="secondary small" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="secondary small"
                        onClick={() => {
                          setChosen(a.departments.map((d) => d.id));
                          setEditing(a.app_user_id);
                        }}
                      >
                        Change
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Which Department Runs What">
        <ErrorBanner error={assign.error} />
        <p className="muted small">
          Until an amenity is assigned it belongs to the office, and a department head
          cannot open it however many departments they hold. This is the row that
          connects the two.
        </p>
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 200 }}>Amenity</th>
              <th>Run by</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m}>
                <td>{m}</td>
                <td>
                  <select
                    value={runsOf(m)}
                    disabled={assign.busy}
                    onChange={async (e) => {
                      if (await assign.run(m, e.target.value || null)) onChange();
                    }}
                  >
                    <option value="">The office</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function PersonLine({
  person: p,
  onIssued,
  onChanged,
}: {
  person: PersonRow;
  onIssued: (i: Issued) => void;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);

  const create = useMutation(async () =>
    api<{ code: string; email: string; expires_in_hours: number }>('/v1/logins', {
      method: 'POST',
      body: { person_id: p.person_id },
    }),
  );

  const newCode = useMutation(async () =>
    api<{ code: string; email: string; expires_in_hours: number }>(
      `/v1/logins/${p.app_user_id}/code`,
      { method: 'POST', body: {} },
    ),
  );

  const withdraw = useMutation(async () =>
    api(`/v1/logins/${p.app_user_id}/code`, { method: 'DELETE' }),
  );

  const change = useMutation(async (body: Record<string, unknown>) =>
    api(`/v1/logins/${p.app_user_id}`, { method: 'PATCH', body }),
  );

  const busy = create.busy || newCode.busy || withdraw.busy || change.busy;

  return (
    <>
      <tr>
        <td data-label="Name" className="cell--title">
          {p.display_name}
          {p.role && p.role !== 'member' && (
            <span className="muted small cell--note"> · {p.role}</span>
          )}
        </td>
        <td data-label="Email" className="muted cell--aside">
          {p.login_email ?? p.person_email ?? <span className="muted">no address on file</span>}
        </td>
        <td data-label="Membership" className="muted cell--aside">
          {p.account_number ? `${p.account_number}${p.category_name ? ` · ${p.category_name}` : ''}` : '—'}
        </td>
        <td data-label="State">
          {!p.has_login ? (
            p.live_code_id ? (
              <Status tone="warning">Code outstanding</Status>
            ) : (
              <Status tone="neutral">No login</Status>
            )
          ) : p.status !== 'active' ? (
            <Status tone="neutral">{p.status}</Status>
          ) : p.is_locked_out ? (
            <Status tone="serious">Locked out</Status>
          ) : p.live_code_id ? (
            <Status tone="warning">
              Code …{p.live_code_last4}
              {Number(p.live_code_attempts) > 0 && ` · ${p.live_code_attempts} wrong tries`}
            </Status>
          ) : (
            <Status tone="good">Can sign in</Status>
          )}
        </td>
        <td data-label="Last signed in" className="muted cell--aside">
          {p.last_login_at ? dateTime(p.last_login_at) : p.has_login ? 'never' : '—'}
        </td>
        <td data-label="">
          {!p.has_login ? (
            <button
              className="secondary small"
              disabled={busy}
              onClick={async () => {
                const r = await create.run();
                if (r) onIssued({ who: p.display_name, ...r });
                onChanged();
              }}
            >
              {create.busy ? 'Creating…' : 'Give them a login'}
            </button>
          ) : (
            <button className="secondary small" onClick={() => setOpen((o) => !o)}>
              {open ? 'Close' : 'Manage'}
            </button>
          )}
        </td>
      </tr>

      {(create.error || newCode.error || withdraw.error || change.error) && (
        <tr>
          <td colSpan={6}>
            <ErrorBanner
              error={create.error ?? newCode.error ?? withdraw.error ?? change.error}
            />
          </td>
        </tr>
      )}

      {open && p.has_login && (
        <tr>
          <td colSpan={6}>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button
                disabled={busy}
                onClick={async () => {
                  const r = await newCode.run();
                  if (r) onIssued({ who: p.display_name, ...r });
                  onChanged();
                }}
              >
                {newCode.busy ? 'Issuing…' : 'Issue a sign-in code'}
              </button>

              {p.live_code_id && (
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={async () => {
                    const r = await withdraw.run();
                    if (r) onChanged();
                  }}
                >
                  {withdraw.busy ? 'Withdrawing…' : `Withdraw the code ending …${p.live_code_last4}`}
                </button>
              )}

              {p.is_locked_out && (
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={async () => {
                    const r = await change.run({ unlock: true });
                    if (r) onChanged();
                  }}
                >
                  Unlock
                </button>
              )}

              <div className="spacer" />

              <label className="row" style={{ gap: 6 }}>
                <span className="muted small">Can act as</span>
                <select
                  value={p.role ?? 'member'}
                  disabled={busy}
                  onChange={async (e) => {
                    const r = await change.run({ role: e.target.value });
                    if (r) onChanged();
                  }}
                >
                  <option value="member">Member</option>
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="controller">Controller</option>
                  <option value="club_admin">Club administrator</option>
                </select>
              </label>

              <button
                className="secondary"
                disabled={busy}
                onClick={async () => {
                  const r = await change.run({
                    status: p.status === 'active' ? 'suspended' : 'active',
                  });
                  if (r) onChanged();
                }}
              >
                {p.status === 'active' ? 'Suspend' : 'Restore'}
              </button>
            </div>

            <p className="muted small" style={{ marginTop: 8, marginBottom: 0 }}>
              A login is suspended, never deleted &mdash; it is the answer to &ldquo;who was
              signing in as that in March&rdquo;, and the audit trail points at it.
              {p.live_code_expires_at && (
                <> The outstanding code expires {fmtDate(p.live_code_expires_at)}.</>
              )}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The code, once.
 *
 * Deliberately not a toast: it has to be dismissed on purpose, because
 * closing it is the last moment anybody can read it.
 */
function CodePanel({ issued, onClose }: { issued: Issued; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <Card title={`Sign-in code for ${issued.who}`}>
      <p className="muted small">
        This is shown once. It is stored as a hash, so nothing here &mdash; including this screen
        &mdash; can show it again, and issuing another one stops this working.
      </p>

      <div className="code-once">{spaced(issued.code)}</div>

      <p className="muted small">
        For <strong>{issued.email}</strong>. Expires in {issued.expires_in_hours} hours. They enter
        it under &ldquo;Forgotten your password?&rdquo; on the sign-in page, with the password they
        want.
      </p>

      <div className="actions">
        <button
          className="secondary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(issued.code);
              setCopied(true);
            } catch {
              // No clipboard permission, or an insecure origin. Not worth
              // an error banner — the code is on the screen either way.
              setCopied(false);
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button onClick={onClose}>I have written it down</button>
      </div>
    </Card>
  );
}
